import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, Tag, Popconfirm, message, Tabs, Badge } from "antd";
import { Plus, Car, AlertTriangle, Wrench, ClipboardCheck } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import dayjs from "dayjs";
import { useVehicleDailyChecksList } from "../../../hooks/api/vehicles/hooks";
import { IVehicleDailyCheck } from "../../../types/api/incident";
import type { ColumnType } from "antd/es/table";
import MyPagination from "../../components/Pagination";
import {
  useVehicleList, useVehicleCreate, useVehicleUpdate, useVehicleDelete,
  useDefectList, useResolveDefect,
  useMileageList, useMileageLogCreate,
} from "../../../hooks/api/vehicles/hooks";
import { useStaffList } from "../../../hooks/api/staff/hooks";
import { IVehicle, IVehicleCreatePayload, IVehicleUpdatePayload, IVehicleDefect, IMileageLogCreatePayload } from "../../../types/api/vehicle";

const FUEL_COLOR: Record<string, string> = { petrol: "red", diesel: "default", electric: "green", hybrid: "blue" };
const SEVERITY_COLOR: Record<string, string> = { minor: "orange", major: "volcano", dangerous: "red" };

const DEFAULT_NUQS_CONFIG = { throttleMs: Infinity };

function VehicleModal({ open, editing, onClose, onSuccess }: { open: boolean; editing: IVehicle | null; onClose: () => void; onSuccess: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const createMut = useVehicleCreate({ onSuccess: () => { msgApi.success("Vehicle created"); onSuccess(); onClose(); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const updateMut = useVehicleUpdate({ onSuccess: () => { msgApi.success("Vehicle updated"); onSuccess(); onClose(); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });
  const { data: staffRes } = useStaffList({ page: 1, limit: 500, status: "ACTIVE" });
  const staff: any[] = (staffRes as any)?.data ?? [];

  const onFinish = (vals: any) => {
    const payload = {
      ...vals,
      mot_due: vals.mot_due ? vals.mot_due.format("YYYY-MM-DD") : undefined,
      tax_due: vals.tax_due ? vals.tax_due.format("YYYY-MM-DD") : undefined,
      insurance_due: vals.insurance_due ? vals.insurance_due.format("YYYY-MM-DD") : undefined,
      service_due: vals.service_due ? vals.service_due.format("YYYY-MM-DD") : undefined,
    };
    if (editing) { updateMut.mutate({ ...payload, id: editing.id }); }
    else { createMut.mutate(payload as IVehicleCreatePayload); }
  };

  return (
    <>
      {ctx}
      <Modal
        title={editing ? "Edit Vehicle" : "Add Vehicle"}
        open={open}
        onCancel={onClose}
        footer={null}
        width={640}
        afterOpenChange={(v) => {
          if (v && editing) {
            form.setFieldsValue({
              ...editing,
              mot_due: editing.mot_due ? dayjs(editing.mot_due) : null,
              tax_due: editing.tax_due ? dayjs(editing.tax_due) : null,
              insurance_due: editing.insurance_due ? dayjs(editing.insurance_due) : null,
              service_due: editing.service_due ? dayjs(editing.service_due) : null,
            });
          } else if (!v) { form.resetFields(); }
        }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="registration" label="Registration" rules={[{ required: true }]}>
              <Input placeholder="AB12 CDE" className="uppercase" />
            </Form.Item>
            <Form.Item name="fuel_type" label="Fuel Type" initialValue="diesel">
              <Select options={[{ value: "petrol", label: "Petrol" }, { value: "diesel", label: "Diesel" }, { value: "electric", label: "Electric" }, { value: "hybrid", label: "Hybrid" }]} />
            </Form.Item>
            <Form.Item name="make" label="Make"><Input placeholder="Ford" /></Form.Item>
            <Form.Item name="model" label="Model"><Input placeholder="Transit" /></Form.Item>
            <Form.Item name="year" label="Year"><Input type="number" placeholder="2022" /></Form.Item>
            <Form.Item name="colour" label="Colour"><Input placeholder="White" /></Form.Item>
            <Form.Item name="vin" label="VIN"><Input placeholder="17-char chassis code" /></Form.Item>
            <Form.Item name="mot_due" label="MOT Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="tax_due" label="Tax Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="insurance_due" label="Insurance Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="service_due" label="Service Due"><DatePicker className="w-full" /></Form.Item>
            <Form.Item name="assigned_user_id" label="Assigned To" className="col-span-2">
              <Select
                allowClear
                placeholder="Unassigned"
                showSearch
                filterOption={(v, o) => ((o?.label as string) ?? "").toLowerCase().includes(v.toLowerCase())}
                options={staff.map((s) => ({ value: s.id, label: `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || s.email }))}
              />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending || updateMut.isPending}>
              {editing ? "Save Changes" : "Add Vehicle"}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

function VehiclesTab() {
  const [editing, setEditing] = useState<IVehicle | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useVehicleList({ page: 1, limit: 20 });
  const items: IVehicle[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const deleteMut = useVehicleDelete({ onSuccess: () => msgApi.success("Vehicle deleted"), onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed") });

  // Matches the backend convention used elsewhere (e.g. asset warranty/insurance
  // status): "expiring_soon" is inclusive of exactly 30 days out, and an already-
  // past due date is its own "expired" state rather than being lumped in with
  // "expiring soon" under the same styling.
  const dueDateStatus = (d: string | null): "expired" | "expiring_soon" | "ok" | null => {
    if (!d) return null;
    const diff = dayjs(d).startOf("day").diff(dayjs().startOf("day"), "day");
    if (diff < 0) return "expired";
    if (diff <= 30) return "expiring_soon";
    return "ok";
  };

  const columns: ColumnType<IVehicle>[] = [
    { title: "Registration", dataIndex: "registration", key: "registration", render: (v) => <span className="font-mono font-bold text-slate-800">{v}</span> },
    { title: "Make / Model", key: "make_model", render: (_, r) => <span>{r.make} {r.model}</span> },
    { title: "Year", dataIndex: "year", key: "year" },
    { title: "Fuel", dataIndex: "fuel_type", key: "fuel_type", render: (v) => <Tag color={FUEL_COLOR[v]}>{v}</Tag> },
    {
      title: "MOT Due", dataIndex: "mot_due", key: "mot_due",
      render: (v) => {
        if (!v) return "—";
        const status = dueDateStatus(v);
        return (
          <span className={status === "expired" || status === "expiring_soon" ? "text-red-600 font-semibold" : ""}>
            {status === "expired" && "EXPIRED — "}{dayjs(v).format("DD MMM YYYY")}
          </span>
        );
      }
    },
    {
      title: "Service Due", dataIndex: "service_due", key: "service_due",
      render: (v) => {
        if (!v) return "—";
        const status = dueDateStatus(v);
        return (
          <span className={status === "expired" ? "text-red-600 font-semibold" : status === "expiring_soon" ? "text-amber-600 font-semibold" : ""}>
            {status === "expired" && "EXPIRED — "}{dayjs(v).format("DD MMM YYYY")}
          </span>
        );
      }
    },
    {
      title: "Tax Due", dataIndex: "tax_due", key: "tax_due",
      render: (v) => {
        if (!v) return "—";
        const status = dueDateStatus(v);
        return (
          <span className={status === "expired" ? "text-red-600 font-semibold" : status === "expiring_soon" ? "text-amber-600 font-semibold" : ""}>
            {status === "expired" && "EXPIRED — "}{dayjs(v).format("DD MMM YYYY")}
          </span>
        );
      }
    },
    {
      title: "Insurance Due", dataIndex: "insurance_due", key: "insurance_due",
      render: (v) => {
        if (!v) return "—";
        const status = dueDateStatus(v);
        return (
          <span className={status === "expired" ? "text-red-600 font-semibold" : status === "expiring_soon" ? "text-amber-600 font-semibold" : ""}>
            {status === "expired" && "EXPIRED — "}{dayjs(v).format("DD MMM YYYY")}
          </span>
        );
      }
    },
    { title: "Assigned To", dataIndex: "assigned_user_name", key: "assigned_user_name", render: (v) => v?.trim() || "—" },
    {
      title: "Status", dataIndex: "is_active", key: "is_active",
      render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete this vehicle?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Vehicles ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">Add Vehicle</ButtonWrapper>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <VehicleModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} onSuccess={() => refetchWithQuery({ page: 1 })} />
    </>
  );
}

function DefectsTab() {
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading } = useDefectList({ page: 1, limit: 50, is_resolved: false });
  const defects: IVehicleDefect[] = (data as any)?.data ?? [];
  const resolveMut = useResolveDefect({ onSuccess: () => msgApi.success("Defect resolved") });

  const columns: ColumnType<IVehicleDefect>[] = [
    { title: "Vehicle", dataIndex: "vehicle_registration", key: "vehicle_registration", render: (v) => <span className="font-mono font-bold">{v}</span> },
    { title: "Area", dataIndex: "defect_area", key: "defect_area" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Severity", dataIndex: "severity", key: "severity", render: (v) => <Tag color={SEVERITY_COLOR[v]}>{v}</Tag> },
    { title: "Reported", dataIndex: "created_at", key: "created_at", render: (v) => dayjs(v).format("DD MMM YYYY") },
    {
      title: "Actions", key: "actions",
      render: (_, r) => !r.is_resolved ? (
        <Popconfirm title="Mark as resolved?" onConfirm={() => resolveMut.mutate(r.id)}>
          <Button size="small" type="primary">Resolve</Button>
        </Popconfirm>
      ) : <Tag color="green">Resolved</Tag>,
    },
  ];

  return (
    <>
      {ctx}
      <div className="mb-4 flex items-center gap-2">
        <AlertTriangle size={16} className="text-amber-500" />
        <h2 className="text-base font-semibold text-slate-700">Open Defects ({defects.length})</h2>
      </div>
      <Table dataSource={defects} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }} />
    </>
  );
}

function MileageTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data, isLoading, refetchWithQuery } = useMileageList({ page: 1, limit: 20 });
  const items = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;
  const createMut = useMileageLogCreate({ onSuccess: () => { msgApi.success("Log added"); setModalOpen(false); form.resetFields(); refetchWithQuery({ page: 1 }); }, onError: (e) => msgApi.error(e?.response?.data?.message || "Error") });

  const columns: ColumnType<any>[] = [
    { title: "Date", dataIndex: "log_date", key: "log_date", render: (v) => dayjs(v).format("DD MMM YYYY") },
    { title: "Vehicle", dataIndex: "vehicle_registration", key: "vehicle_registration", render: (v) => <span className="font-mono font-bold">{v}</span> },
    { title: "Driver", dataIndex: "user_name", key: "user_name" },
    { title: "Start", dataIndex: "start_odometer", key: "start_odometer", render: (v) => `${v} mi` },
    { title: "End", dataIndex: "end_odometer", key: "end_odometer", render: (v) => `${v} mi` },
    { title: "Distance", dataIndex: "distance_miles", key: "distance_miles", render: (v) => <span className="font-semibold">{v} mi</span> },
    { title: "Type", dataIndex: "journey_type", key: "journey_type", render: (v) => <Tag color={v === "business" ? "blue" : "default"}>{v}</Tag> },
    { title: "From", dataIndex: "from_location", key: "from_location", render: (v) => v || "—" },
    { title: "To", dataIndex: "to_location", key: "to_location", render: (v) => v || "—" },
  ];

  const onFinish = (vals: any) => {
    createMut.mutate({
      ...vals,
      log_date: vals.log_date.format("YYYY-MM-DD"),
      distance_miles: vals.end_odometer - vals.start_odometer,
    } as IMileageLogCreatePayload);
  };

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Mileage Logs ({total})</h2>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => setModalOpen(true)} className="flex items-center gap-2">Log Mileage</ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} pagination={false} scroll={{ x: "max-content" }}
        footer={() => <MyPagination qData={qData} total={total} DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG} onChange={refetchWithQuery} />}
      />
      <Modal title="Log Mileage" open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={520}>
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="vehicle_id" label="Vehicle ID" rules={[{ required: true }]}>
              <Input type="number" placeholder="Vehicle ID" />
            </Form.Item>
            <Form.Item name="log_date" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="start_odometer" label="Start Odometer" rules={[{ required: true }]}>
              <Input type="number" placeholder="Miles" />
            </Form.Item>
            <Form.Item name="end_odometer" label="End Odometer" rules={[{ required: true }]}>
              <Input type="number" placeholder="Miles" />
            </Form.Item>
            <Form.Item name="journey_type" label="Journey Type" initialValue="business">
              <Select options={[{ value: "business", label: "Business" }, { value: "personal", label: "Personal" }]} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="from_location" label="From"><Input placeholder="Starting location" /></Form.Item>
            <Form.Item name="to_location" label="To"><Input placeholder="Destination" /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMut.isPending}>Save Log</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

function DailyChecksTab() {
  const { data, isLoading, refetchWithQuery } = useVehicleDailyChecksList({ page: 1, limit: 20 });
  const items: IVehicleDailyCheck[] = (data as any)?.data ?? [];
  const qData = (data as any)?.qData ?? { page: 1, limit: 20 };
  const total = qData?.total ?? 0;

  const columns = [
    {
      title: "Date", dataIndex: "check_date", key: "check_date",
      render: (v: string) => dayjs(v).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "Vehicle", key: "vehicle",
      render: (_: any, r: IVehicleDailyCheck) => (
        <div>
          <div className="font-mono font-bold text-slate-800">{r.registration}</div>
          <div className="text-xs text-slate-400">{r.make} {r.model}</div>
        </div>
      ),
    },
    { title: "Driver", dataIndex: "driver_name", key: "driver_name", render: (v: string) => v || "—" },
    {
      title: "Type", dataIndex: "check_type", key: "check_type",
      render: (v: string) => <Tag color="blue">{v.replace("_", " ")}</Tag>,
    },
    {
      title: "Result", dataIndex: "overall_status", key: "overall_status",
      render: (v: string) => (
        <Tag color={v === "pass" ? "green" : v === "fail" ? "red" : "orange"}>
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Odometer", dataIndex: "odometer", key: "odometer",
      render: (v: number | null) => v ? `${v.toLocaleString()} mi` : "—",
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-700">Daily Vehicle Checks ({total})</h2>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 20, total, showTotal: (t) => `${t} checks`, showSizeChanger: false, onChange: (p) => refetchWithQuery({ page: p, limit: 20 }) }}
        scroll={{ x: "max-content" }}
        rowClassName={(r) => r.overall_status === "fail" ? "bg-red-50/40" : r.overall_status === "advisory" ? "bg-amber-50/40" : ""}
      />
    </>
  );
}

const FleetPage = () => {
  const tabItems = [
    { key: "vehicles", label: <span className="flex items-center gap-1.5"><Car size={13} />Vehicles</span>, children: <VehiclesTab /> },
    { key: "daily-checks", label: <span className="flex items-center gap-1.5"><ClipboardCheck size={13} />Daily Checks</span>, children: <DailyChecksTab /> },
    { key: "defects", label: <span className="flex items-center gap-1.5"><AlertTriangle size={13} />Defects</span>, children: <DefectsTab /> },
    { key: "mileage", label: <span className="flex items-center gap-1.5"><Wrench size={13} />Mileage Logs</span>, children: <MileageTab /> },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800">Fleet Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage vehicles, defect checks and mileage logs</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <Tabs defaultActiveKey="vehicles" items={tabItems} />
      </div>
    </div>
  );
};

export default FleetPage;
